import * as math from 'mathjs';
import { RowType } from '../types';
import type { RecipeStore } from './RecipeStore';
import type { GroupStore } from './GroupStore';
import type { RowStore } from './RowStore';
import { log } from '@/lib/utils/logger';

/**
 * FormulaScope for MobX-based recipes.
 *
 * Direct port from Knockout's FormulaScope (client/src/app/viewModel/recipe/FormulaScope.ts).
 * Main change: MobX observables instead of ko.observable/ko.unwrap.
 *
 * Provides formula functions:
 * - cell(row, column) - get value from specific row/column
 * - group(groupSymbol, column, row?) - get value(s) from another group
 * - lookup(field, column, row?, group?) - lookup field from lookup column
 *
 * MobX automatically tracks dependencies when formulas access observables.
 * This gives us Knockout's automatic recalculation behavior in React.
 */
export class FormulaScope {
  public const: Record<string, any> = {};

  private recipe: RecipeStore;
  private currentGroup?: GroupStore;
  private currentRow?: RowStore;
  private calcRuntimeFormula?: string;

  constructor(recipe: RecipeStore, formulaConstants?: Record<string, any>, calcRuntimeFormula?: string) {
    this.recipe = recipe;
    this.const = formulaConstants || {};
    this.calcRuntimeFormula = calcRuntimeFormula;
  }

  /**
   * Apply group/row context for formula evaluation.
   * Same pattern as Knockout's FormulaScope.apply(object).
   */
  apply(group?: GroupStore, row?: RowStore): FormulaScope {
    const copy = new FormulaScope(this.recipe, this.const, this.calcRuntimeFormula);
    copy.currentGroup = group || this.currentGroup;
    copy.currentRow = row || this.currentRow;
    return copy;
  }

  /**
   * Evaluate a formula string in the current context
   *
   * @param formula The formula to evaluate
   * @param row The row context for evaluation
   * @param excludeSymbol Optional symbol to exclude from scope (the column being calculated to prevent false circular deps)
   */
  evaluateFormula(formula: string, row: RowStore, excludeSymbol?: string): any {
    try {
      // Build scope object with functions and variables
      const rowValuesScope = this.getRowValuesScope(row, excludeSymbol);

      const scope: any = {
        ...this.const,
        const: this.const, // For bracket notation like const[elem]
        // MathJS functions (same as Knockout imports)
        unit: math.unit,
        number: math.number,
        abs: math.abs,
        round: math.round,
        floor: math.floor,
        ceil: math.ceil,
        min: math.min,
        max: math.max,
        sqrt: math.sqrt,
        pow: math.pow,
        exp: math.exp,
        log: math.log,
        log10: math.log10,
        sin: math.sin,
        cos: math.cos,
        tan: math.tan,
        pi: math.pi,
        e: math.e,
        sum: math.sum,
        mean: math.mean,
        median: math.median,
        std: math.std,
        // Custom recipe functions
        cell: (rowSymbol: string, colSymbol: string) => this.cell(rowSymbol, colSymbol),
        duration: (rowSymbol: string, colSymbol: string, unit?: string) => this.duration(rowSymbol, colSymbol, unit),
        group: (groupSymbol: string, colSymbol: string, rowSymbol?: string) =>
          this.group(groupSymbol, colSymbol, rowSymbol),
        lookup: (field: string, column: string, rowSymbol?: string, groupSymbol?: string) =>
          this.lookup(field, column, rowSymbol, groupSymbol),
      };

      // Assign row values scope without spreading (preserves lazy getters)
      Object.setPrototypeOf(scope, rowValuesScope);

      let result = math.evaluate(formula, scope);

      // Extract numeric value from MathJS Unit objects (e.g., "5 hours in days" → 0.208...)
      // Direct port from Knockout RecipeRowVM.ts line 383-385
      if (math.typeOf(result) === 'Unit') {
        result = (result as any).toNumber();
      }

      return result;
    } catch (error: any) {
      // MathJS throws specific errors for undefined variables or missing data
      // These are expected when formulas reference fields that haven't been filled yet
      const isExpectedError =
        error.data?.category === 'wrongType' ||
        error.message?.includes('identifier') ||
        error.message?.includes('undefined') ||
        error.message?.includes('Unexpected type of argument');

      if (isExpectedError) {
        // Some data this formula depends on hasn't been entered yet - this is normal
        return undefined;
      }

      // Only log unexpected errors in development for debugging
      if (process.env.NODE_ENV === 'development') {
        log.warn(`[FormulaScope] Error evaluating formula "${formula}":`, error?.message || error);
      }
      return undefined;
    }
  }

  /**
   * duration() function for formulas - calculate duration between current row and next row.
   * Direct port from Knockout's FormulaScope.duration().
   *
   * Calculates the duration from the current row's DateTime value to the next row's DateTime value.
   *
   * In the formula `duration(symbol, "ts", "min")`, the `symbol` parameter is the value of the
   * symbol column (e.g., "1", "2", etc.), but we use the current row context to find the actual row.
   *
   * @param _rowSymbol Row symbol value (from the symbol column, kept for API compatibility but not used)
   * @param columnSymbol Column symbol for the DateTime column (e.g., "ts")
   * @param unit Optional unit for the result (e.g., "min", "hour", "day", "m:s"). Defaults to "m:s" format.
   */
  private duration(_rowSymbol: string, columnSymbol: string, unit?: string): any {
    if (!this.currentGroup || !this.currentRow) {
      log.error('[FormulaScope] duration() called without group or row context');
      return undefined;
    }

    if (this.currentGroup.rows.length === 0) {
      return undefined;
    }

    // Use current row (where the formula is being evaluated)
    const row = this.currentRow;

    // Find next row by index (current row index + 1)
    const currentIndex = this.currentGroup.rows.indexOf(row);
    if (currentIndex === -1) {
      return undefined;
    }

    const nextIndex = currentIndex + 1;
    if (nextIndex >= this.currentGroup.rows.length) {
      // Last row - no next row
      return undefined;
    }

    const nextRow = this.currentGroup.rows[nextIndex];

    // Get column to check if it's a DateTime column
    const col = this.currentGroup.getColumn(columnSymbol);
    if (!col || col.type !== 'D') {
      // Not a DateTime column
      return undefined;
    }

    // Get values (already converted to milliseconds by getValueForCalculations)
    const value = row.getValueForCalculations(columnSymbol);
    const nextValue = nextRow.getValueForCalculations(columnSymbol);

    if (value === undefined || value === null || nextValue === undefined || nextValue === null) {
      return undefined;
    }

    // Calculate difference in milliseconds
    const diffMs = nextValue - value;

    // Convert to requested unit
    switch (unit) {
      case 's':
      case 'sec':
        return Math.floor(diffMs / 1000);
      case 'm':
      case 'min':
        return diffMs / (1000 * 60); // Return decimal minutes
      case 'h':
      case 'hours':
        return diffMs / (1000 * 60 * 60); // Return decimal hours
      case 'd':
      case 'days':
        return diffMs / (1000 * 60 * 60 * 24); // Return decimal days
      case 'm:s':
      case 'min:sec':
      default:
        // Default format: "H:mm:ss" or "mm:ss" if less than an hour
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }

  /**
   * cell() function for formulas - get value from specific row/column.
   * Direct port from Knockout's FormulaScope.cell().
   */
  private cell(rowSymbol: string, columnSymbol: string): any {
    if (!this.currentGroup) {
      log.error('[FormulaScope] cell() called without group context');
      return undefined;
    }

    const row = this.currentGroup.getRow(rowSymbol);
    if (!row) {
      log.warn(`[FormulaScope] Row ${rowSymbol} not found in group ${this.currentGroup.symbol}`);
      return undefined;
    }

    // MobX will automatically track this dependency!
    return row.getValueForCalculations(columnSymbol);
  }

  /**
   * group() function for formulas - get value(s) from another group.
   * Direct port from Knockout's FormulaScope.group().
   *
   * - With rowSymbol: returns single value
   * - Without rowSymbol: returns array (for aggregation like sum())
   * - RowType.One groups: automatically returns first row
   */
  private group(groupSymbol: string, columnSymbol: string, rowSymbol?: string): any {
    const group = this.recipe.getGroup(groupSymbol);
    if (!group) {
      log.warn(`[FormulaScope] Group ${groupSymbol} not found`);
      return rowSymbol ? undefined : [];
    }

    // If group has RowType.One or rowSymbol specified, return single value
    if (rowSymbol || group.rowType === RowType.One) {
      const row = rowSymbol ? group.getRow(rowSymbol) : group.rows[0];
      if (!row) {
        log.warn(`[FormulaScope] Row ${rowSymbol} not found in group ${groupSymbol}`);
        return undefined;
      }
      return row.getValueForCalculations(columnSymbol);
    }

    // Array of all values in that column (for aggregation like sum())
    // MobX will track dependencies on all these rows!
    return group.rows
      .map((row) => row.getValueForCalculations(columnSymbol))
      .filter((value) => value !== undefined && value !== null && value !== '');
  }

  /**
   * lookup() function for formulas - get field from lookup column's selected object.
   * Direct port from Knockout's FormulaScope.lookup().
   */
  private lookup(field: string, columnSymbol: string, rowSymbol?: string, groupSymbol?: string): any {
    let group: GroupStore | undefined;
    let row: RowStore | undefined;

    // Resolve group
    if (groupSymbol) {
      group = this.recipe.getGroup(groupSymbol);
    } else {
      group = this.currentGroup;
    }

    if (!group) {
      log.warn('[FormulaScope] lookup() called without group context');
      return undefined;
    }

    // Resolve row
    if (rowSymbol) {
      row = group.getRow(rowSymbol);
    } else {
      row = this.currentRow;
    }

    if (!row) {
      log.warn('[FormulaScope] lookup() called without row context');
      return undefined;
    }

    // Get the ID/value from the lookup column
    const lookupId = row.getValue(columnSymbol);
    if (lookupId === undefined || lookupId === null) {
      return undefined;
    }

    // Get the column definition to find dropdown values
    const column = group.getColumn(columnSymbol);
    if (!column || !column.isLookup) {
      log.warn(`[FormulaScope] Column ${columnSymbol} is not a lookup column`);
      return undefined;
    }

    // Find the lookup object in dropdown values
    const lookupObj = column.dropdownValues.find((item: any) => item.id === lookupId);
    if (!lookupObj) {
      log.warn(`[FormulaScope] Lookup object with id ${lookupId} not found in column ${columnSymbol}`);
      return undefined;
    }

    // Navigate the field path (e.g., "properties.purity")
    const fieldPath = field.split('.');
    let result: any = lookupObj;

    for (const part of fieldPath) {
      if (result && typeof result === 'object' && part in result) {
        result = result[part];
      } else {
        // Field not found
        return undefined;
      }
    }

    return result ?? undefined;
  }

  /**
   * Mathjs reserved keywords that cannot be used as variable names in scope.
   * Columns with these names can still be accessed via functions like cell(), group(), etc.
   */
  private static readonly MATHJS_RESERVED = new Set([
    'end',
    'in',
    'and',
    'not',
    'or',
    'xor',
    'true',
    'false',
    'null',
    'undefined',
  ]);

  /**
   * Build scope with all column values for direct variable access in formulas.
   * Allows formulas like "T_K * 0.1" or "calculated_mass * 2" where symbols refer to columns.
   *
   * Uses LAZY getters to prevent infinite recursion:
   * - Getters only evaluate when accessed by the formula
   * - getValue() uses cache to prevent redundant calculations
   * - Excludes the column being evaluated to prevent false circular dependency detection
   * - Filters out MathJS reserved keywords (can still access via cell/group functions)
   *
   * @param row The row to get values from
   * @param excludeSymbol Optional symbol to exclude (the column being calculated)
   */
  private getRowValuesScope(row: RowStore, excludeSymbol?: string): Record<string, any> {
    const scope: Record<string, any> = {};

    // Add ALL column values (manual AND formula) as lazy getters
    if (this.currentGroup) {
      for (const column of this.currentGroup.columns) {
        // Skip: the column being evaluated, OR MathJS reserved keywords
        const isReserved = FormulaScope.MATHJS_RESERVED.has(column.symbol);
        const isExcluded = column.symbol === excludeSymbol;

        if (column.symbol && !isReserved && !isExcluded) {
          // Use Object.defineProperty to create a lazy getter
          // This only calls getValueForCalculations when the formula actually accesses the symbol
          Object.defineProperty(scope, column.symbol, {
            get: () => {
              const value = row.getValueForCalculations(column.symbol);
              // Return undefined instead of empty strings for math operations
              return value === '' || value === null ? undefined : value;
            },
            enumerable: true,
            configurable: true,
          });
        }
      }
    }

    return scope;
  }
}
